/**
 * Buildings & Zones (Hooldusraamat / EVS 807)
 *
 * Optional hierarchy on top of equipment:
 *   building (Hoone) -> zone / room (Tsoon, Ruum) -> equipment (Seade)
 *
 * Equipment with no building_id keeps working exactly as before — the
 * hierarchy is an organising layer, never a requirement.
 */

import { t } from '../i18n.js'
import { escapeHtml } from '../utils.js'

function refreshIcons() {
  if (typeof lucide !== 'undefined') lucide.createIcons()
}

// Cached for the location picker and for rendering breadcrumbs on the
// equipment list without a round-trip per row.
export let buildingsCache = []
export let zonesCache = []

function buildingById(id) {
  return buildingsCache.find(b => b.id === id) || null
}

function zoneById(id) {
  return zonesCache.find(z => z.id === id) || null
}

/** Building name for one equipment row, or '-' when unassigned (export-friendly). */
export function buildingNameFor(equipment) {
  return buildingById(equipment.building_id)?.name || '-'
}

/** Zone name for one equipment row, or '-' when unassigned (export-friendly). */
export function zoneNameFor(equipment) {
  return zoneById(equipment.zone_id)?.name || '-'
}

/**
 * Human-readable "Building > Zone" for one equipment row.
 * Returns '' when the equipment has no location assigned.
 */
export function locationLabel(equipment) {
  const parts = []
  const building = equipment.building_id ? buildingById(equipment.building_id) : null
  const zone = equipment.zone_id ? zoneById(equipment.zone_id) : null
  if (building) parts.push(building.name)
  if (zone) parts.push(zone.name)
  return parts.join(' › ')
}

export function locationBreadcrumbHtml(equipment) {
  const label = locationLabel(equipment)
  if (!label) return ''
  return `<span class="location-breadcrumb"><i data-lucide="map-pin"></i>${escapeHtml(label)}</span>`
}

// ==================== LOAD ====================

export async function loadBuildings() {
  const container = document.getElementById('buildingsList')

  try {
    const { supabase, currentUser } = await import('./main.js')
    if (!currentUser?.tenant_id) return

    const [{ data: buildings, error: bErr }, { data: zones, error: zErr }] = await Promise.all([
      supabase.from('buildings').select('*').eq('tenant_id', currentUser.tenant_id).order('name'),
      supabase.from('zones').select('*').eq('tenant_id', currentUser.tenant_id).order('name'),
    ])

    if (bErr) throw bErr
    if (zErr) throw zErr

    buildingsCache = buildings || []
    zonesCache = zones || []

    const countEl = document.getElementById('totalBuildings')
    if (countEl) countEl.textContent = buildingsCache.length

    // Equipment counts per building / zone, for the tree badges.
    const { data: equipment } = await supabase
      .from('equipment')
      .select('id, building_id, zone_id')
      .eq('tenant_id', currentUser.tenant_id)

    renderBuildings(container, equipment || [])
  } catch (error) {
    console.error('Error loading buildings:', error)
    if (container) {
      container.innerHTML = `<p class="buildings-empty">${escapeHtml(error.message || 'Error')}</p>`
    }
  }
}

function renderBuildings(container, equipment) {
  if (!container) return

  if (buildingsCache.length === 0) {
    container.innerHTML = `
      <div class="buildings-empty">
        <p>${t('buildings.emptyTitle')}</p>
        <p class="text-muted">${t('buildings.emptyHint')}</p>
      </div>`
    return
  }

  const countFor = (predicate) => equipment.filter(predicate).length

  container.innerHTML = buildingsCache.map(building => {
    const buildingZones = zonesCache.filter(z => z.building_id === building.id)
    const totalCount = countFor(e => e.building_id === building.id)
    const unzonedCount = countFor(e => e.building_id === building.id && !e.zone_id)

    const zonesHtml = buildingZones.length === 0
      ? `<p class="text-muted" style="margin:4px 0 10px;">${t('zones.empty')}</p>`
      : buildingZones.map(zone => `
          <div class="zone-row">
            <i data-lucide="door-open"></i>
            <div class="zone-row-title" style="cursor:pointer;"
                 onclick="showEquipmentInLocation('${building.id}', '${zone.id}')">
              ${escapeHtml(zone.name)}
              ${zone.description ? `<small>${escapeHtml(zone.description)}</small>` : ''}
            </div>
            <span class="zone-count">${countFor(e => e.zone_id === zone.id)} ${escapeHtml(t('buildings.itemsShort'))}</span>
            <button class="btn btn-sm btn-secondary" onclick="editZone('${zone.id}')" title="${escapeHtml(t('common.edit'))}">
              <i data-lucide="pencil"></i>
            </button>
            <button class="btn btn-sm btn-danger" onclick="deleteZone('${zone.id}')" title="${escapeHtml(t('common.delete'))}">
              <i data-lucide="trash-2"></i>
            </button>
          </div>`).join('')

    return `
      <div class="building-card">
        <div class="building-card-header">
          <i data-lucide="building-2"></i>
          <div class="building-card-title" style="cursor:pointer;"
               onclick="showEquipmentInLocation('${building.id}')">
            <strong>${escapeHtml(building.name)}</strong>
            <span>${escapeHtml(building.address || '')}</span>
          </div>
          <span class="zone-count">${totalCount} ${escapeHtml(t('buildings.itemsShort'))}</span>
          <div class="building-card-actions">
            <button class="btn btn-sm btn-primary" onclick="addZone('${building.id}')">
              <i data-lucide="plus"></i> ${escapeHtml(t('zones.addBtn'))}
            </button>
            <button class="btn btn-sm btn-secondary" onclick="exportBuildingBook('${building.id}', this)"
                    title="${escapeHtml(t('hooldusraamat.exportHint'))}">
              <i data-lucide="book-text"></i> ${escapeHtml(t('hooldusraamat.exportBtn'))}
            </button>
            <button class="btn btn-sm btn-secondary" onclick="editBuilding('${building.id}')" title="${escapeHtml(t('common.edit'))}">
              <i data-lucide="pencil"></i>
            </button>
            <button class="btn btn-sm btn-danger" onclick="deleteBuilding('${building.id}')" title="${escapeHtml(t('common.delete'))}">
              <i data-lucide="trash-2"></i>
            </button>
          </div>
        </div>
        <div class="building-card-body">
          ${zonesHtml}
          ${unzonedCount > 0
            ? `<p class="text-muted" style="margin-top:8px;">${escapeHtml(t('zones.unassignedCount').replace('{count}', unzonedCount))}</p>`
            : ''}
          ${building.notes ? `<p class="text-muted" style="margin-top:8px;">${escapeHtml(building.notes)}</p>` : ''}
        </div>
      </div>`
  }).join('')

  refreshIcons()
}

// ==================== BUILDING CRUD ====================

function openBuildingModal(building = null) {
  const modal = document.getElementById('buildingModal')
  if (!modal) return

  document.getElementById('buildingModalTitle').textContent =
    building ? t('buildings.editBtn') : t('buildings.addBtn')
  document.getElementById('buildingId').value = building?.id || ''
  document.getElementById('buildingName').value = building?.name || ''
  document.getElementById('buildingAddress').value = building?.address || ''
  document.getElementById('buildingNotes').value = building?.notes || ''
  document.getElementById('buildingError').textContent = ''

  modal.style.display = 'flex'
  document.getElementById('buildingName').focus()
}

function closeBuildingModal() {
  const modal = document.getElementById('buildingModal')
  if (modal) modal.style.display = 'none'
}

async function saveBuilding(event) {
  event.preventDefault()

  const form = event.target
  const submitBtn = form.querySelector('button[type="submit"]')
  const btnText = document.getElementById('buildingBtnText')
  const errorEl = document.getElementById('buildingError')

  const id = document.getElementById('buildingId').value
  const payload = {
    name: document.getElementById('buildingName').value.trim(),
    address: document.getElementById('buildingAddress').value.trim() || null,
    notes: document.getElementById('buildingNotes').value.trim() || null,
  }

  if (!payload.name) return

  errorEl.textContent = ''
  btnText.textContent = t('common.saving')
  submitBtn.disabled = true

  try {
    const { supabase, currentUser } = await import('./main.js')

    const { error } = id
      ? await supabase.from('buildings').update(payload).eq('id', id)
      : await supabase.from('buildings').insert({ ...payload, tenant_id: currentUser.tenant_id })

    if (error) throw error

    closeBuildingModal()
    await loadBuildings()
  } catch (error) {
    console.error('Error saving building:', error)
    errorEl.textContent = error.message || t('common.error')
  } finally {
    btnText.textContent = t('common.save')
    submitBtn.disabled = false
  }
}

async function deleteBuilding(id) {
  const building = buildingById(id)
  if (!building) return
  if (!confirm(t('buildings.deleteConfirm').replace('{name}', building.name))) return

  try {
    const { supabase } = await import('./main.js')
    // zones cascade; equipment.building_id / zone_id fall back to NULL
    const { error } = await supabase.from('buildings').delete().eq('id', id)
    if (error) throw error
    await loadBuildings()
    window.loadEquipmentList?.()
  } catch (error) {
    console.error('Error deleting building:', error)
    alert(error.message || t('common.error'))
  }
}

// ==================== ZONE CRUD ====================

function openZoneModal(buildingId, zone = null) {
  const modal = document.getElementById('zoneModal')
  if (!modal) return

  const building = buildingById(buildingId)

  document.getElementById('zoneModalTitle').textContent =
    zone ? t('zones.editBtn') : t('zones.addBtn')
  document.getElementById('zoneModalBuilding').textContent = building?.name || ''
  document.getElementById('zoneId').value = zone?.id || ''
  document.getElementById('zoneBuildingId').value = buildingId
  document.getElementById('zoneName').value = zone?.name || ''
  document.getElementById('zoneDescription').value = zone?.description || ''
  document.getElementById('zoneError').textContent = ''

  modal.style.display = 'flex'
  document.getElementById('zoneName').focus()
}

function closeZoneModal() {
  const modal = document.getElementById('zoneModal')
  if (modal) modal.style.display = 'none'
}

async function saveZone(event) {
  event.preventDefault()

  const form = event.target
  const submitBtn = form.querySelector('button[type="submit"]')
  const btnText = document.getElementById('zoneBtnText')
  const errorEl = document.getElementById('zoneError')

  const id = document.getElementById('zoneId').value
  const buildingId = document.getElementById('zoneBuildingId').value
  const payload = {
    name: document.getElementById('zoneName').value.trim(),
    description: document.getElementById('zoneDescription').value.trim() || null,
  }

  if (!payload.name || !buildingId) return

  errorEl.textContent = ''
  btnText.textContent = t('common.saving')
  submitBtn.disabled = true

  try {
    const { supabase, currentUser } = await import('./main.js')

    const { error } = id
      ? await supabase.from('zones').update(payload).eq('id', id)
      : await supabase.from('zones').insert({
          ...payload,
          building_id: buildingId,
          tenant_id: currentUser.tenant_id,
        })

    if (error) throw error

    closeZoneModal()
    await loadBuildings()
  } catch (error) {
    console.error('Error saving zone:', error)
    errorEl.textContent = error.message || t('common.error')
  } finally {
    btnText.textContent = t('common.save')
    submitBtn.disabled = false
  }
}

async function deleteZone(id) {
  const zone = zoneById(id)
  if (!zone) return
  if (!confirm(t('zones.deleteConfirm').replace('{name}', zone.name))) return

  try {
    const { supabase } = await import('./main.js')
    const { error } = await supabase.from('zones').delete().eq('id', id)
    if (error) throw error
    await loadBuildings()
    window.loadEquipmentList?.()
  } catch (error) {
    console.error('Error deleting zone:', error)
    alert(error.message || t('common.error'))
  }
}

// ==================== REUSABLE BUILDING / ZONE SELECT PAIR ====================

/**
 * Populate a building <select> and its dependent zone <select>, and keep them
 * in sync. Used by the add- and edit-equipment forms.
 */
export function populateLocationSelects(buildingSelectId, zoneSelectId, buildingId = null, zoneId = null) {
  const buildingSelect = document.getElementById(buildingSelectId)
  const zoneSelect = document.getElementById(zoneSelectId)
  if (!buildingSelect || !zoneSelect) return

  buildingSelect.innerHTML = [
    `<option value="">${escapeHtml(t('buildings.none'))}</option>`,
    ...buildingsCache.map(b =>
      `<option value="${b.id}"${b.id === buildingId ? ' selected' : ''}>${escapeHtml(b.name)}</option>`
    ),
  ].join('')

  syncZoneSelect(buildingSelectId, zoneSelectId, zoneId)
}

function syncZoneSelect(buildingSelectId, zoneSelectId, zoneId = null) {
  const buildingSelect = document.getElementById(buildingSelectId)
  const zoneSelect = document.getElementById(zoneSelectId)
  if (!buildingSelect || !zoneSelect) return

  const buildingId = buildingSelect.value || null

  zoneSelect.innerHTML = [
    `<option value="">${escapeHtml(t('zones.none'))}</option>`,
    ...zonesCache
      .filter(z => z.building_id === buildingId)
      .map(z => `<option value="${z.id}"${z.id === zoneId ? ' selected' : ''}>${escapeHtml(z.name)}</option>`),
  ].join('')

  zoneSelect.disabled = !buildingId
}

/** Wire the change handler once, at startup. */
export function wireLocationSelects(buildingSelectId, zoneSelectId) {
  document.getElementById(buildingSelectId)?.addEventListener('change', () => {
    syncZoneSelect(buildingSelectId, zoneSelectId, null)
  })
}

/** Read the chosen location back out. A zone is only valid inside its building. */
export function readLocationSelects(buildingSelectId, zoneSelectId) {
  const buildingId = document.getElementById(buildingSelectId)?.value || null
  const zoneId = buildingId ? (document.getElementById(zoneSelectId)?.value || null) : null
  return { building_id: buildingId, zone_id: zoneId }
}

// ==================== ASSIGN EQUIPMENT TO A LOCATION ====================

export async function openAssignLocation(equipmentId) {
  const modal = document.getElementById('assignLocationModal')
  if (!modal) return

  try {
    const { supabase } = await import('./main.js')

    // Buildings may not be loaded yet if the admin never opened the tab.
    if (buildingsCache.length === 0) await loadBuildings()

    const { data: equipment, error } = await supabase
      .from('equipment')
      .select('id, name, building_id, zone_id')
      .eq('id', equipmentId)
      .single()

    if (error) throw error

    document.getElementById('assignLocationEquipmentId').value = equipment.id
    document.getElementById('assignLocationEquipmentName').textContent = equipment.name || ''
    document.getElementById('assignLocationError').textContent = ''

    populateLocationSelects(
      'assignBuildingSelect',
      'assignZoneSelect',
      equipment.building_id,
      equipment.zone_id
    )

    modal.style.display = 'flex'
  } catch (error) {
    console.error('Error opening location modal:', error)
    alert(error.message || t('common.error'))
  }
}

function closeAssignLocation() {
  const modal = document.getElementById('assignLocationModal')
  if (modal) modal.style.display = 'none'
}

async function saveAssignLocation(event) {
  event.preventDefault()

  const form = event.target
  const submitBtn = form.querySelector('button[type="submit"]')
  const btnText = document.getElementById('assignLocationBtnText')
  const errorEl = document.getElementById('assignLocationError')

  const equipmentId = document.getElementById('assignLocationEquipmentId').value
  const { building_id: buildingId, zone_id: zoneId } =
    readLocationSelects('assignBuildingSelect', 'assignZoneSelect')

  errorEl.textContent = ''
  btnText.textContent = t('common.saving')
  submitBtn.disabled = true

  try {
    const { supabase } = await import('./main.js')
    const { error } = await supabase
      .from('equipment')
      .update({ building_id: buildingId, zone_id: zoneId })
      .eq('id', equipmentId)

    if (error) throw error

    closeAssignLocation()
    window.loadEquipmentList?.()
    await loadBuildings()
  } catch (error) {
    console.error('Error saving location:', error)
    errorEl.textContent = error.message || t('common.error')
  } finally {
    btnText.textContent = t('common.save')
    submitBtn.disabled = false
  }
}

// ==================== WIRING ====================

export function initBuildings() {
  document.getElementById('addBuildingBtn')?.addEventListener('click', () => openBuildingModal())
  document.getElementById('closeBuildingModal')?.addEventListener('click', closeBuildingModal)
  document.getElementById('cancelBuildingBtn')?.addEventListener('click', closeBuildingModal)
  document.getElementById('buildingForm')?.addEventListener('submit', saveBuilding)

  document.getElementById('closeZoneModal')?.addEventListener('click', closeZoneModal)
  document.getElementById('cancelZoneBtn')?.addEventListener('click', closeZoneModal)
  document.getElementById('zoneForm')?.addEventListener('submit', saveZone)

  document.getElementById('closeAssignLocationModal')?.addEventListener('click', closeAssignLocation)
  document.getElementById('cancelAssignLocationBtn')?.addEventListener('click', closeAssignLocation)
  document.getElementById('assignLocationForm')?.addEventListener('submit', saveAssignLocation)

  // Building -> zone dependency for every building/zone select pair
  wireLocationSelects('assignBuildingSelect', 'assignZoneSelect')
  wireLocationSelects('addBuildingSelect', 'addZoneSelect')
  wireLocationSelects('editBuildingSelect', 'editZoneSelect')

  // Inline onclick handlers in the rendered tree
  window.editBuilding = (id) => openBuildingModal(buildingById(id))
  window.deleteBuilding = deleteBuilding
  window.addZone = (buildingId) => openZoneModal(buildingId)
  window.editZone = (id) => {
    const zone = zoneById(id)
    if (zone) openZoneModal(zone.building_id, zone)
  }
  window.deleteZone = deleteZone
  window.openAssignLocation = openAssignLocation
}
